import React, { useState, useEffect, useMemo } from 'react'
import { endOfToday } from 'date-fns'
import PropTypes from 'prop-types'
import ReactTooltip from 'react-tooltip'
import { useTranslation } from 'react-i18next'

import SelectField from '../../../FormLegacy/FormFieldsV2/SelectField'
import TextField from '../../../FormLegacy/FormFieldsV2/TextField'
import EditorText from '../../../EditorText/EditorText'
import ConfirmPopup from '../../../Popups/ConfirmPopup'
import Icon from '../../../Icons'
import PicturesModal from '../../../Modal/PicturesModal'
import API from '../../../../api/api'
import { EMAIL_REGEX } from '../../../../helpers/constants'
import { isMatchExtension } from '../../../../helpers/helpers'
import { getAssetLink } from '../../../../helpers/get-asset-link'
import Filter from '../../../Filter/Filter'
import ScheduleSetupModal from '../ScheduleSetupModal'
import {
  renderSelectBuildingPlaceholder,
  hasOneBuildingAvailable,
  selectBuildingOptions,
  getScheduleMinTime,
  pluck,
  getFilesValidationMessage,
  getEmailFromInputOptions,
} from '../helpers'
import RecipientsFilter from '../RecipientsFilter'
import { RecipientLocationFilters } from '../RecipientLocationFilters'
import { useRecipientLocationFilters } from '../useRecipientLocationFilters'
import DateField from '../../../FormLegacy/FormFieldsV2/DateField'
import { useSnackbar } from '../../../../helpers/hooks/useSnackbar'

const EmailOrNotificationTemplate = ({
  buildings,
  selectedBuilding,
  mailingListOptions,
  templateType,
  previewData,
  isDuplicateAnnouncementFormActive,
  selectedMailingList,
  setSelectedMailingList,
  getMailingListOptionsIds,
  editorBody,
  setEditorBody,
  isSubmitting,
  timeout,
  setFieldTouched,
  handleSubmit,
  setFieldValue,
  values,
  isDisabled,
  handleChange,
  touched,
  errors,
  setSubmitting,
  handleCancel,
  refreshTable,
  userRole,
}) => {
  const { t } = useTranslation(['organisation', 'common'])
  const isCompleted = previewData && previewData.status === 'Completed'
  const isFieldDisabled =
    isSubmitting ||
    (isCompleted && !isDuplicateAnnouncementFormActive) ||
    (previewData && !values.deliveryTime && !isDuplicateAnnouncementFormActive)
  const [isNewFromLabel, setIsNewFromLabel] = useState(false)
  const [isModalConfirmOpened, setIsModalConfirmOpened] = useState(false)
  const [testEmail, setTestEmail] = useState('')
  const [showScheduleSetupModal, setShowScheduleSetupModal] = useState(false)
  const [isPicturesModalVisible, setPicturesModalVisible] = useState(false)
  const [picturesToShow, setPicturesToShow] = useState([])
  const { displaySnackbar } = useSnackbar()

  const displayErrorSnackbar = (errorMessage) => {
    displaySnackbar(
      errorMessage || t('organisation:communicationPortal.newMessageForm.genericError'),
      true,
    )
  }

  const fromInputOptions = useMemo(() => {
    return getEmailFromInputOptions(selectedBuilding.text, t)
  }, [selectedBuilding, t])

  useEffect(() => {
    return () => {
      if (timeout.current) clearTimeout(timeout.current)
    }
  }, [])

  const submitTestEmail = (values, setSubmitting) => {
    setSubmitting(true)
    const sendData = {
      ...values,
      testEmail,
      mailingList: getMailingListOptionsIds(),
      clientIds: pluck(values.clients),
      userGroups: pluck(values.userGroups, 'value'),
      unitTypes: pluck(values.unitTypes),
      clientTags: pluck(values.clientTags, 'value'),
      communityGroups: pluck(values.communityGroups),
      body: editorBody.content,
      header: '?',
      footer: '?',
    }

    API.sendTestEmail(sendData, selectedBuilding.id)
      .then(() => {
        setSubmitting(false)
      })
      .catch((error) => {
        setSubmitting(false)
        displayErrorSnackbar(error.response?.data?.message)
      })
  }

  const submitPreviewEmail = (values, setSubmitting) => {
    setSubmitting(true)
    const sendData = {
      ...values,
      mailingList: getMailingListOptionsIds(),
      clientIds: pluck(values.clients),
      userGroups: pluck(values.userGroups, 'value'),
      unitTypes: pluck(values.unitTypes),
      clientTags: pluck(values.clientTags, 'value'),
      communityGroups: pluck(values.communityGroups),
      body: editorBody.content,
      header: '?',
      footer: '?',
    }

    API.sendPreviewEmail(sendData, selectedBuilding.id)
      .then((response) => {
        setSubmitting(false)
        var newWindow = window.open()
        newWindow.document.write(response.data)
        newWindow.document.close()
      })
      .catch((error) => {
        setSubmitting(false)
        displayErrorSnackbar(error.response?.data?.message)
      })
  }

  const handlePickerAttachment = (e, isMultiFiles) => {
    if (isMultiFiles) {
      const files = Array.from(e.target.files)
      setFieldValue('attachments', [...values.attachments, ...files])
    } else {
      const file = e.target.files[0]
      setFieldValue('attachments', [file])
    }
  }

  const removeFile = (id) => {
    setFieldValue(
      'attachments',
      values.attachments.filter((file, i) => i !== id),
    )
  }

  const isFromFieldToShow = useMemo(() => {
    return values.buildings.length <= 1 && !values.buildings.find((building) => building.isTag)
  }, [values.buildings])

  const filesValidation = useMemo(
    () => getFilesValidationMessage(values.attachments, isDuplicateAnnouncementFormActive, t),
    [values.attachments, isDuplicateAnnouncementFormActive, t],
  )

  const handleSetupSchedule = () => {
    handleSubmit()
    setShowScheduleSetupModal(false)
  }

  const handleConfirmCallback = () => {
    const removeScheduledCommunicationMessagePromise =
      templateType === 'email'
        ? API.removeScheduledEmail(previewData.id)
        : API.removeScheduledNotification(previewData.id)

    removeScheduledCommunicationMessagePromise
      .then(() => {
        setSubmitting(false)
        handleCancel()
        refreshTable()
      })
      .catch((error) => {
        setSubmitting(false)
        displayErrorSnackbar(error.response?.data?.message)
      })
  }

  const onPreviewAttachment = (file) => {
    const fileName = file.name || file.fileName
    if (isMatchExtension(fileName, ['png', 'jpg', 'jpeg'])) {
      const images = values.attachments
        .filter((file) => {
          const imageName = file.name || file.fileName
          return isMatchExtension(imageName, ['png', 'jpg', 'jpeg']) && imageName !== fileName
        })
        .map((file) => file.name || file.fileName)
      setPicturesToShow([fileName, ...images])
      setPicturesModalVisible(true)
    } else if (isMatchExtension(fileName, ['pdf'])) {
      window.open(
        getAssetLink({
          path: 'chitown-images/notification',
          file: fileName,
          fallback: { file: 'notification_image.png' },
        }),
        '_blank',
      )
    }
  }
  const {
    yardiPropertyOptions,
    buildingOptions,
    floorOptions,
    isYardiProperty,
    resetLocationFilters,
  } = useRecipientLocationFilters({
    selectedProperties: values.buildings,
    previewData,
    handleChange,
    setFieldValue,
  })

  const handleBuildingsChange = (e) => {
    handleChange(e)
    resetLocationFilters()
  }

  const prevDataClients = useMemo(() => {
    const userGroupIds = previewData?.recipientTypes?.userGroupIds || []
    const standaloneClientIds = previewData?.standaloneClientIds || []
    const communityGroups = previewData?.recipientTypes?.communityGroups || []
    const clientTags = previewData?.recipientTypes?.clientTags || []
    const unitTypes = previewData?.recipientTypes?.unitTypes || []

    if (userGroupIds.length || standaloneClientIds.length) {
      // Note: Group and client ids overlap (group 2 vs client 2), so they're kept apart
      return {
        type: 'residents',
        userGroupIds,
        standaloneClientIds,
        standaloneClients: previewData?.standaloneClients,
      }
    } else if (communityGroups.length) {
      return {
        type: 'groups',
        optionIds: communityGroups,
      }
    } else if (clientTags.length) {
      return {
        type: 'clientTags',
        optionIds: clientTags,
      }
    } else if (unitTypes.length) {
      return {
        type: 'units',
        optionIds: unitTypes,
      }
    }
  }, [previewData])

  const shouldDisplayScheduleForInput =
    values.deliveryTime && !isDuplicateAnnouncementFormActive && !isCompleted
  const isFromInputDisabled = isSubmitting || (!!previewData && !isDuplicateAnnouncementFormActive)
  const shouldSendTestEmailSection =
    (!previewData || isDuplicateAnnouncementFormActive) && templateType === 'email'
  const shouldDisplayWindowFooter = !previewData || isDuplicateAnnouncementFormActive

  useEffect(() => {
    if (!previewData?.from) {
      return
    }

    const predefinedBuildingSelected = !!fromInputOptions.find(
      (option) => option.value === previewData?.from,
    )
    setIsNewFromLabel(!predefinedBuildingSelected)
  }, [previewData, buildingOptions, fromInputOptions])

  return (
    <React.Fragment>
      <div className={`main-view ${templateType}-inner`}>
        <div className="field-wrapper property-filters">
          <Filter
            name="buildings"
            placeholder={renderSelectBuildingPlaceholder(values.buildings, t)}
            additionalClass="select-tags-or-buildings-dropdown"
            options={selectBuildingOptions(buildings, userRole, t)}
            value={values.buildings}
            onChange={handleBuildingsChange}
            config={{
              multiple: true,
              hasSearch: true,
            }}
            disabled={hasOneBuildingAvailable(buildings, userRole)}
          />
          <RecipientLocationFilters
            values={values}
            handleChange={handleChange}
            isYardiProperty={isYardiProperty}
            yardiPropertyOptions={yardiPropertyOptions}
            buildingOptions={buildingOptions}
            floorOptions={floorOptions}
          />
        </div>
        <div className="field-wrapper">
          <RecipientsFilter
            handleChange={handleChange}
            messagesType={templateType}
            isDisabled={isFieldDisabled}
            values={values}
            prevDataClients={prevDataClients}
          />
        </div>
        <div className="field-wrapper">
          <div className="field-title">
            {t('organisation:communicationPortal.emailTemplate.mailingListsLabel')}
          </div>
          <SelectField
            name={'mailingList'}
            value={selectedMailingList}
            placeholder={t(
              'organisation:communicationPortal.emailTemplate.selectMailingListPlaceholder',
            )}
            options={mailingListOptions}
            onChange={(e) => setSelectedMailingList(e.target.value)}
            disabled={isFieldDisabled}
          />
          {!getMailingListOptionsIds() && (
            <div className="error-message">
              {t('organisation:communicationPortal.emailTemplate.noMailingListHint')}
            </div>
          )}
        </div>
        {templateType === 'email' && (
          <React.Fragment>
            {isFromFieldToShow && (
              <div className="field-wrapper">
                <div className="field-title__static-width">
                  {t('organisation:communicationPortal.emailTemplate.fromLabel')}
                </div>
                {isNewFromLabel ? (
                  <TextField
                    name={'from'}
                    placeholder={t(
                      'organisation:communicationPortal.emailTemplate.newSenderNamePlaceholder',
                    )}
                    value={values.from}
                    isError={touched.from && !!errors.from}
                    onChange={handleChange}
                    onBlur={() => setFieldTouched('from')}
                    autoComplete="new-password"
                    disabled={isFromInputDisabled}
                    autoFocus
                  />
                ) : (
                  <SelectField
                    name="from"
                    value={values.from}
                    options={fromInputOptions}
                    onChange={() => {
                      setIsNewFromLabel(true)
                      setFieldValue('from', '')
                    }}
                    disabled={isFromInputDisabled}
                  />
                )}
              </div>
            )}
            <div className="field-wrapper">
              <div className="field-title__static-width">
                {t('organisation:communicationPortal.emailTemplate.replyToLabel')}
              </div>
              <TextField
                name={'replyTo'}
                placeholder={''}
                style={{ width: '100%' }}
                value={values.replyTo}
                isError={touched.replyTo && !!errors.replyTo}
                onChange={handleChange}
                onBlur={() => setFieldTouched('replyTo')}
                autoComplete="new-password"
                disabled={isFieldDisabled}
              />
            </div>
          </React.Fragment>
        )}
        <div className="field-wrapper">
          <div className="field-title__static-width">
            {t('organisation:communicationPortal.emailTemplate.subjectLabel')}
          </div>
          <TextField
            name={'title'}
            placeholder={''}
            style={{ width: '100%' }}
            value={values.title}
            isError={touched.title && !!errors.title}
            errorLabel={errors.title}
            onChange={handleChange}
            onBlur={() => setFieldTouched('title')}
            autoComplete="new-password"
            disabled={isFieldDisabled}
          />
        </div>
        <div
          className={`editor-container ${
            templateType === 'notification' || (templateType === 'email' && previewData)
              ? 'without-toolbar'
              : ''
          } ${previewData ? 'disabled' : ''} ${isFromFieldToShow ? 'decreased-height' : ''}`}
        >
          <EditorText
            disabled={isFieldDisabled}
            value={editorBody.content}
            onChange={(content, text) => {
              setEditorBody({ content, text })
              if (templateType === 'notification') {
                setFieldValue('message', text)
              }
            }}
            shouldClearTheEditor={!previewData ? !!templateType : false}
            imageType={templateType === 'email' ? 'email_attachments' : ''}
          />
          {!!errors.message && <span className={'label--error'}>{errors.message}</span>}
        </div>
        {values.attachments && values.attachments.length ? (
          <div className="field-wrapper" style={{ border: 'none', paddingBottom: '0' }}>
            <div className="files-container">
              {values.attachments.map((file, i) => {
                return (
                  <div
                    key={i}
                    className="file-container"
                    style={{ cursor: previewData ? 'pointer' : 'auto' }}
                    onClick={() => {
                      if (previewData && !isDuplicateAnnouncementFormActive) {
                        onPreviewAttachment(file)
                      }
                    }}
                  >
                    <div className="attachment-data">
                      <Icon
                        icon="attachment"
                        fill="none"
                        svgProps={{
                          stroke: '#8D8C8C',
                          strokeWidth: '1.81824',
                          strokeLinecap: 'round',
                          strokeLinejoin: 'round',
                        }}
                      />
                      <span className="file-name-label">{file.name || file.fileName}</span>
                    </div>
                    {(!previewData || isDuplicateAnnouncementFormActive) && (
                      <div className="remove-icon" onClick={() => removeFile(i)}>
                        <Icon icon="cross" />
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        ) : null}
        {previewData && previewData.image && (
          <div className="field-wrapper" style={{ border: 'none', paddingBottom: '0' }}>
            <div className="files-container">
              <div
                className={`file-container ${previewData ? 'preview' : ''}`}
                onClick={() => onPreviewAttachment({ name: previewData.image })}
              >
                <div className="attachment-data">
                  <Icon
                    icon="attachment"
                    fill="none"
                    svgProps={{
                      stroke: '#8D8C8C',
                      strokeWidth: '1.81824',
                      strokeLinecap: 'round',
                      strokeLinejoin: 'round',
                    }}
                  />
                  <span className="file-name-label">{previewData.image}</span>
                </div>
              </div>
            </div>
          </div>
        )}
        {shouldSendTestEmailSection ? (
          <React.Fragment>
            <div className="field-wrapper">
              <label className="icon-container" htmlFor="file-input">
                <div data-tip data-for="max-size-tip">
                  <Icon icon="upload" />
                  <ReactTooltip className="tooltip" id="max-size-tip" multiline place="top">
                    {t('organisation:communicationPortal.emailTemplate.maxAttachmentSizeTooltip')}
                  </ReactTooltip>
                </div>
              </label>
              <input
                id="file-input"
                type="file"
                name="files"
                accept="application/pdf, image/png, image/jpeg, image/jpg"
                multiple
                onChange={(e) => handlePickerAttachment(e, true)}
              />
            </div>
            <div className="field-wrapper" style={{ justifyContent: 'space-between' }}>
              <div className="field-title">
                {t('organisation:communicationPortal.emailTemplate.sendTestEmailLabel')}
              </div>
              <div className="flex-right">
                <TextField
                  name={'testEmail'}
                  placeholder={t('organisation:communicationPortal.emailTemplate.emailPlaceholder')}
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  autoComplete="new-password"
                  disabled={isSubmitting || isDisabled}
                />
                <button
                  type="submit"
                  className={`button button-black is-block ${isSubmitting ? 'is-loading' : ''}`}
                  onClick={() => submitTestEmail(values, setSubmitting)}
                  disabled={
                    isSubmitting || isDisabled || !testEmail || !EMAIL_REGEX.test(testEmail)
                  }
                >
                  {t('organisation:communicationPortal.emailTemplate.sendTest')}
                </button>
              </div>
            </div>
          </React.Fragment>
        ) : !previewData && templateType === 'email' ? (
          <div className="field-wrapper" style={{ border: 'none' }}>
            <label className="icon-container" htmlFor="file-input">
              <div data-tip data-for="max-size-tip">
                <Icon icon="picture" />
                <ReactTooltip className="tooltip" id="max-size-tip" multiline place="top">
                  {t('organisation:communicationPortal.emailTemplate.maxAttachmentSizeTooltip')}
                </ReactTooltip>
              </div>
            </label>
            <input
              id="file-input"
              type="file"
              name="files"
              accept="image/png, image/jpeg, image/jpg"
              onChange={(e) => handlePickerAttachment(e, false)}
            />
          </div>
        ) : null}
        {/* --- SCHEDULE FOR --- */}
        {shouldDisplayScheduleForInput && (
          <div className="edit-window-footer">
            <div className="footer-inputs">
              <div className="field-title__static-width">
                {t('organisation:communicationPortal.emailTemplate.scheduledForLabel')}
              </div>
              <DateField
                name="deliveryDate"
                onChange={handleChange}
                minDate={new Date()}
                value={values.deliveryDate}
                placeholderText={t(
                  'organisation:communicationPortal.emailTemplate.selectDeliveryDatePlaceholder',
                )}
                additionalClass="reservation-picker"
              />
              <DateField
                name="deliveryTime"
                placeholderText={t(
                  'organisation:communicationPortal.emailTemplate.selectDeliveryTimePlaceholder',
                )}
                value={values.deliveryTime}
                onChange={handleChange}
                popperClassName="schedule-setup__container__wide-date-field"
                minTime={getScheduleMinTime(values.deliveryDate, selectedBuilding.timezone)}
                maxTime={endOfToday()}
                showTimeSelect
                showTimeSelectOnly
                timeIntervals={30}
                timeCaption="Start Time"
                dateFormat="hh:mm a"
              />
            </div>
            <div className="footer-buttons">
              <button
                className={'button button-red inverted-colors is-block'}
                onClick={() => setIsModalConfirmOpened(true)}
              >
                {t('common:actions.delete')}
              </button>
              <button className={'button button-gray is-block'} onClick={handleSubmit}>
                {t('common:actions.edit')}
              </button>
            </div>
          </div>
        )}
      </div>
      {shouldDisplayWindowFooter && (
        <div className="window-footer">
          <div className="error-message">{filesValidation.message}</div>
          <div className="footer-buttons">
            {templateType === 'email' && (
              <button
                type="submit"
                className={`button button-black is-block ${isSubmitting ? 'is-loading' : ''}`}
                onClick={() => submitPreviewEmail(values, setSubmitting)}
                disabled={isSubmitting || isDisabled || filesValidation.isError}
              >
                {t('organisation:communicationPortal.emailTemplate.seePreview')}
              </button>
            )}

            <button
              className={`button button-white is-block ${isSubmitting ? 'is-loading' : ''}`}
              onClick={() => setShowScheduleSetupModal(true)}
              disabled={isSubmitting || isDisabled}
            >
              {t('organisation:communicationPortal.emailTemplate.schedule')}
            </button>
            <button
              type="submit"
              className={`button button-black is-block ${isSubmitting ? 'is-loading' : ''}`}
              onClick={handleSubmit}
              disabled={isSubmitting || isDisabled || filesValidation.isError}
            >
              {t('organisation:communicationPortal.smsTemplate.send')}
            </button>
          </div>
        </div>
      )}

      {showScheduleSetupModal && (
        <ScheduleSetupModal
          handleCancel={() => setShowScheduleSetupModal(false)}
          handleSubmit={handleSetupSchedule}
          values={values}
          handleChange={handleChange}
          isSubmitting={isSubmitting}
          isDisabled={isDisabled}
          setSubmitting={setSubmitting}
          setFieldValue={setFieldValue}
          templateType={templateType}
        />
      )}

      <ConfirmPopup
        title={t('organisation:communicationPortal.emailTemplate.deleteConfirm.title', {
          type: templateType,
        })}
        cancelText={t('common:actions.cancel')}
        confirmText={t('common:actions.confirm')}
        isOpen={isModalConfirmOpened}
        cancelCallback={() => setIsModalConfirmOpened(false)}
        confirmCallback={handleConfirmCallback}
      />
      <PicturesModal
        assetPath="chitown-images/notification"
        fallback="notification_image.png"
        visible={isPicturesModalVisible}
        pictures={picturesToShow}
        onCloseModal={() => {
          setPicturesModalVisible(false)
          setPicturesToShow([])
        }}
      />
    </React.Fragment>
  )
}

EmailOrNotificationTemplate.propTypes = {
  buildings: PropTypes.array.isRequired,
  selectedBuilding: PropTypes.object,
  mailingListOptions: PropTypes.array,
  templateType: PropTypes.string,
  previewData: PropTypes.object,
  isDuplicateAnnouncementFormActive: PropTypes.bool,
  selectedMailingList: PropTypes.string,
  setSelectedMailingList: PropTypes.func,
  getMailingListOptionsIds: PropTypes.func,
  editorBody: PropTypes.object,
  setEditorBody: PropTypes.func,
  isSubmitting: PropTypes.bool,
  timeout: PropTypes.object,
  setFieldTouched: PropTypes.func,
  handleSubmit: PropTypes.func,
  setFieldValue: PropTypes.func,
  values: PropTypes.object,
  isDisabled: PropTypes.bool,
  handleChange: PropTypes.func,
  touched: PropTypes.object,
  errors: PropTypes.object,
  setSubmitting: PropTypes.func,
  refreshTable: PropTypes.func,
  handleCancel: PropTypes.func,
  userRole: PropTypes.string,
}

export default EmailOrNotificationTemplate
